// ABOUTME: Central data service for dashboard CRUD operations against Firestore.
// ABOUTME: Instant first paint and offline writes come from the SDK's persistent cache (see services/firebase).

import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  getDocs,
  writeBatch,
  runTransaction,
} from "firebase/firestore";
import { db } from "./firebase";
import { mergeLayout } from "../utils/dashboardLayout";

export interface DashboardItem {
  id: string;
  type: "link" | "widget";
  col: number;
  order: number;
  createdDate?: number;
  url?: string;
  name?: string;
  description?: string;
  tags?: string[];
  bgColor?: string;
  isAd?: boolean;
  widgetName?: string;
}

/**
 * Firestore rejects `undefined` anywhere in a document ("Unsupported field
 * value: undefined"), and an edit that clears an optional field — a link's
 * custom color, say — arrives as `{ bgColor: undefined }`. Dropping those keys
 * makes "cleared" mean "removed" instead of failing the whole save.
 */
export function compactItem(item: DashboardItem): DashboardItem {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(item)) {
    if (value !== undefined) out[key] = value;
  }
  return out as unknown as DashboardItem;
}

function compactItems(items: DashboardItem[]): DashboardItem[] {
  return items.map(compactItem);
}

export const dashboardService = {
  /**
   * Loads the dashboard state for a user (all dashboard items: links + widgets + layout)
   */
  async getDashboard(userId: string): Promise<DashboardItem[] | null> {
    const docRef = doc(db, `users/${userId}/dashboard`, "state");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().items || [];
    }
    return null;
  },

  /**
   * Replaces the whole dashboard. Only for flows that own the full list, such
   * as the guest migration; layout changes go through applyLayout so they
   * cannot clobber items another device added in the meantime.
   */
  async setDashboard(userId: string, items: DashboardItem[]): Promise<void> {
    const docRef = doc(db, `users/${userId}/dashboard`, "state");
    try {
      await setDoc(docRef, { items: compactItems(items) });
    } catch (error) {
      console.error("Error saving dashboard:", error);
      throw error;
    }
  },

  /**
   * Subscribes to real-time dashboard state changes for a user.
   *
   * With the SDK's persistent cache on, the first snapshot comes from
   * IndexedDB before the network answers, so a returning device paints at
   * once; a later snapshot carries the server copy if it differs.
   *
   * `onError` fires when the listener itself fails (permission denied, bad
   * config). Without it, a failed listener used to leave the dashboard on
   * the empty state for good with nothing in the console.
   */
  subscribeToDashboard(
    userId: string,
    callback: (items: DashboardItem[]) => void,
    onError?: (error: Error) => void
  ) {
    const docRef = doc(db, `users/${userId}/dashboard`, "state");
    return onSnapshot(
      docRef,
      (docSnap) => {
        callback(docSnap.exists() ? docSnap.data().items || [] : []);
      },
      (error) => {
        console.error("Dashboard subscription failed:", error);
        onError?.(error);
      }
    );
  },

  /**
   * Moves items to the positions in `desired`, merging by id inside a
   * transaction. Drag-and-drop and column changes used to write the client's
   * whole list over the document, which deleted anything another device had
   * added since the client last synced. See utils/dashboardLayout.
   */
  async applyLayout(userId: string, desired: DashboardItem[]): Promise<void> {
    if (!userId) throw new Error("User not authenticated");

    const docRef = doc(db, `users/${userId}/dashboard`, "state");

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];
      const { items, changed } = mergeLayout(currentItems, desired);
      if (!changed) return;
      transaction.set(docRef, { items });
    });
  },

  /**
   * Adds a new item to the dashboard
   */
  async addDashboardItem(userId: string, item: Omit<DashboardItem, "id">): Promise<string> {
    if (!userId) throw new Error("User not authenticated");

    const docRef = doc(db, `users/${userId}/dashboard`, "state");
    const newId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];

      const newItem: DashboardItem = compactItem({
        ...item,
        id: newId,
        createdDate: Date.now(),
      });

      transaction.set(docRef, { items: [...currentItems, newItem] });
    });

    return newId;
  },

  /**
   * Appends many items in a single transaction.
   *
   * Bookmark import used to call addDashboardItem in a loop, so a typical
   * 800-entry browser export became 800 sequential read-modify-writes of the
   * whole document — minutes of wall clock, and a partial import if it tripped
   * the 500-item rules cap partway through.
   */
  async addDashboardItems(userId: string, items: Omit<DashboardItem, "id">[]): Promise<number> {
    if (!userId) throw new Error("User not authenticated");
    if (items.length === 0) return 0;

    const docRef = doc(db, `users/${userId}/dashboard`, "state");
    const createdDate = Date.now();

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];

      const newItems: DashboardItem[] = items.map((item) =>
        compactItem({
          ...item,
          id: crypto.randomUUID
            ? crypto.randomUUID()
            : `${createdDate}-${Math.random().toString(36).slice(2, 11)}`,
          createdDate,
        })
      );

      transaction.set(docRef, { items: [...currentItems, ...newItems] });
    });

    return items.length;
  },

  /**
   * Updates an existing dashboard item
   */
  async updateDashboardItem(
    userId: string,
    itemId: string,
    updates: Partial<DashboardItem>
  ): Promise<void> {
    if (!userId) throw new Error("User not authenticated");

    const docRef = doc(db, `users/${userId}/dashboard`, "state");

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];
      const updatedItems = currentItems.map((item) =>
        item.id === itemId ? compactItem({ ...item, ...updates }) : item
      );
      transaction.set(docRef, { items: updatedItems });
    });
  },

  /**
   * Removes an item from the dashboard
   */
  async deleteDashboardItem(userId: string, itemId: string): Promise<void> {
    if (!userId) throw new Error("User not authenticated");

    const docRef = doc(db, `users/${userId}/dashboard`, "state");

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];
      const updatedItems = currentItems.filter((item) => item.id !== itemId);
      transaction.set(docRef, { items: updatedItems });
    });
  },

  /**
   * Updates the position (order and column) of a dashboard item
   */
  async updateItemPosition(
    userId: string,
    itemId: string,
    col: number,
    order: number
  ): Promise<void> {
    return this.updateDashboardItem(userId, itemId, { col, order });
  },

  /**
   * Deletes all links from the dashboard for a user (removes all items of type 'link')
   * Also deletes all links in the legacy urls collection for full cleanup.
   */
  async deleteAllLinks(userId: string): Promise<void> {
    if (!userId) throw new Error("User not authenticated");

    const docRef = doc(db, `users/${userId}/dashboard`, "state");

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentItems: DashboardItem[] = docSnap.exists() ? docSnap.data().items || [] : [];
      const filteredItems = currentItems.filter((item) => item.type !== "link");
      transaction.set(docRef, { items: filteredItems });
    });

    // Also delete all legacy links in users/{userId}/urls
    const urlsRef = collection(db, `users/${userId}/urls`);
    const urlsSnapshot = await getDocs(urlsRef);
    if (!urlsSnapshot.empty) {
      const batch = writeBatch(db);
      urlsSnapshot.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });
      await batch.commit();
    }
  },

  /**
   * Migrates existing URLs from the old structure to the new dashboard structure
   * This is a one-time migration function
   */
  async migrateUrlsToDashboard(userId: string): Promise<void> {
    if (!userId) return;

    try {
      // Get current dashboard items
      const currentItems = (await this.getDashboard(userId)) || [];

      // Get all URLs from the old structure
      const urlsRef = collection(db, `users/${userId}/urls`);
      const q = query(urlsRef, orderBy("order", "asc"));
      const urlsSnapshot = await getDocs(q);

      if (urlsSnapshot.empty) {
        return;
      }

      // Check if we already have migrated these URLs (by checking IDs)
      const existingIds = new Set(currentItems.map((item) => item.id));

      // Map URL documents to DashboardItem objects, ensuring all required fields are present
      const urlsToMigrate = urlsSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            type: "link" as const,
            url: data.url,
            name: data.name || data.url || "",
            col: typeof data.col === "number" ? data.col : 0,
            order: typeof data.order === "number" ? data.order : 0,
            tags: data.tags || [],
            createdDate: data.createdDate || Date.now(),
          } as DashboardItem;
        })
        .filter((url) => !existingIds.has(url.id));

      if (urlsToMigrate.length === 0) {
        return; // No new URLs to migrate
      }

      // Add URLs to dashboard items
      const updatedItems = [...currentItems, ...urlsToMigrate];
      await this.setDashboard(userId, updatedItems);

      // Optionally, delete the old URLs (commented out for safety)
      // const batch = writeBatch(db);
      // urlsToMigrate.forEach(url => {
      //   const urlRef = doc(db, `users/${userId}/urls/${url.id}`);
      //   batch.delete(urlRef);
      // });
      // await batch.commit();
    } catch (error) {
      console.error("Error migrating URLs to dashboard:", error);
      throw error;
    }
  },
};
